import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@project/hooks/useAuth";

import { PageShell } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Badge } from "@project/components/ui/badge";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";
import {
  HandHeart,
  Receipt,
  LogOut,
  Loader2,
  User as UserIcon,
  IndianRupee,
  Repeat,
  Calendar,
  Pause,
  Play,
  XCircle,
  Save,
  Edit,
  Sparkles,
  MapPin,
  ClipboardList,
  Users,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { downloadReceiptPDF } from "@project/lib/receipt-pdf";
import { useI18n } from "@project/lib/use-i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";

import {
  linkAndGetDonor,
  getDonorOverview,
  updateDonorProfile,
  setRecurringStatus,
} from "@project/lib/donor-portal.functions";
import { userDetailsService } from "@project/services/user/user-details.service";
import { eventRegistrationService } from "@project/services/admin/event-registration.service";
import { eventService } from "@project/services/admin/event.service";

const inr = (n: number) => `₹${Number(n ?? 0).toLocaleString("en-IN")}`;

export default function AccountPage() {
  const { t } = useI18n();
  const nav = useNavigate();

  const [email, setEmail] = useState<string | null>(null);
  const [name, setName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [donor, setDonor] = useState<any | null>(null);
  const [userDetails, setUserDetails] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [stats, setStats] = useState<any>({
    lifetime_total: 0,
    lifetime_count: 0,
    fy_total: 0,
  });
  const [donations, setDonations] = useState<any[]>([]);
  const [recurring, setRecurring] = useState<any[]>([]);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [eventsMap, setEventsMap] = useState<Record<number, any>>({});

  const {
    isAuthenticated,
    user: authUser,
    logout: authLogout,
    updateSessionUser,
  } = useAuth();

  const completionPercentage = useMemo(() => {
    let score = 0;
    if (authUser?.first_name) {
      score += 10;
    }
    if (authUser?.last_name) {
      score += 10;
    }
    if (authUser?.phone_number) {
      score += 10;
    }
    if (authUser?.email) {
      score += 10;
    }
    if (authUser?.gender && authUser.gender !== "—" && authUser.gender !== "") {
      score += 10;
    }
    if (
      authUser?.date_of_birth &&
      authUser.date_of_birth !== "—" &&
      authUser.date_of_birth !== ""
    ) {
      score += 10;
    }

    if (userDetails?.address_line_1) {
      score += 10;
    }
    if (
      userDetails?.city &&
      userDetails.city !== "—" &&
      userDetails.city !== ""
    ) {
      score += 10;
    }
    if (
      userDetails?.state &&
      userDetails.state !== "—" &&
      userDetails.state !== ""
    ) {
      score += 10;
    }
    if (
      userDetails?.pincode &&
      userDetails.pincode !== "—" &&
      userDetails.pincode !== ""
    ) {
      score += 10;
    }

    return Math.min(100, Math.max(0, score));
  }, [authUser, userDetails]);

  const refresh = async () => {
    try {
      const overview = await getDonorOverview();
      setDonor(overview.donor);
      setStats(overview.stats ?? {});
      setDonations(overview.donations ?? []);
      setRecurring(overview.recurring ?? []);
    } catch {
      // Fallback
    }

    try {
      const details = await userDetailsService.get();
      setUserDetails(details ?? null);
    } catch (err) {
      console.error("Failed to load user details:", err);
    }

    try {
      const regRes = await eventRegistrationService.listPage({
        page_size: 200,
      });
      setRegistrations(regRes.data ?? []);
    } catch (err) {
      console.error("Failed to load registrations:", err);
    }

    try {
      const evtRes = await eventService.listPage({ page_size: 200 });
      const map: Record<number, any> = {};
      if (evtRes.data) {
        evtRes.data.forEach((e: any) => {
          map[e.id] = e;
        });
      }
      setEventsMap(map);
    } catch (err) {
      console.error("Failed to load events list:", err);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      nav({ to: "/login", search: { redirectTo: "/account" } });
      return;
    }

    setEmail(authUser?.email ?? authUser?.phone_number ?? null);
    setName(
      authUser?.first_name
        ? `${authUser.first_name} ${authUser.last_name ?? ""}`.trim()
        : (authUser?.username ?? t("account.account.hero.donorFallback"))
    );

    (async () => {
      try {
        await linkAndGetDonor();
      } catch (e) {
        console.error(e);
      }
      await refresh();
      setLoading(false);
    })();
  }, [isAuthenticated, authUser]);

  useEffect(() => {
    if (!loading) {
      setActiveTab(donor ? "donations" : "profile");
    }
  }, [loading, donor]);

  const signOut = async () => {
    authLogout();
    toast.success(t("account.account.toast.signedOut"));
    nav({ to: "/" });
  };

  const downloadOne = (d: any) => {
    downloadReceiptPDF({
      receipt_no: d.receipt_no,
      donation_date: d.donation_date,
      amount: d.amount,
      payment_mode: d.payment_mode,
      transaction_id: d.transaction_id,
      purpose: d.purpose,
      category: d.category,
      requires_80g: d.requires_80g,
      donor: d.donor,
    });
  };

  // Removed unused yearly statement downloads

  return (
    <PageShell>
      <section className="from-primary/5 to-gold/10 border-b bg-gradient-to-br via-background">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
                <UserIcon className="h-7 w-7" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("account.account.hero.myAccount")}
                </p>
                <h1 className="text-2xl font-bold sm:text-3xl">
                  {donor?.full_name ?? name}
                </h1>
                <p className="text-sm text-muted-foreground">{email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button asChild className="rounded-full">
                <Link to="/donate">
                  <HandHeart className="mr-2 h-4 w-4" />
                  {t("account.account.hero.donateAgain")}
                </Link>
              </Button>
              <Button
                onClick={signOut}
                variant="outline"
                className="rounded-full"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t("account.account.hero.signOut")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="grid min-h-[40vh] place-items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gold" />
        </div>
      ) : (
        <>
          {donor && (
            <section className="mx-auto grid max-w-6xl gap-4 px-4 py-8 sm:grid-cols-4">
              <StatCard
                icon={<IndianRupee className="h-4 w-4" />}
                label={t("account.account.stats.lifetimeTotal")}
                value={inr(Number(stats.lifetime_total ?? 0))}
              />
              <StatCard
                icon={<Receipt className="h-4 w-4" />}
                label={t("account.account.stats.donations")}
                value={String(stats.lifetime_count ?? 0)}
              />
              <StatCard
                icon={<Calendar className="h-4 w-4" />}
                label={t("account.account.stats.thisFy")}
                value={inr(Number(stats.fy_total ?? 0))}
              />
              <StatCard
                icon={<Repeat className="h-4 w-4" />}
                label={t("account.account.stats.activeRecurring")}
                value={String(
                  recurring.filter(r => r.status === "active").length
                )}
              />
            </section>
          )}

          <section className="mx-auto max-w-6xl px-4 pb-16 pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:grid-cols-4">
                <TabsTrigger value="donations">
                  {t("account.account.tabs.donations")}
                </TabsTrigger>
                <TabsTrigger value="recurring">
                  {t("account.account.tabs.recurring")}
                </TabsTrigger>
                <TabsTrigger value="events">Event Registrations</TabsTrigger>
                <TabsTrigger value="profile">
                  {t("account.account.tabs.profile")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="donations" className="mt-4">
                <DonationsTable rows={donations} onDownload={downloadOne} />
              </TabsContent>

              <TabsContent value="recurring" className="mt-4">
                <RecurringTable
                  rows={recurring}
                  onAction={async (id, status) => {
                    try {
                      await setRecurringStatus({ data: { id, status } });
                      toast.success(
                        t("account.account.toast.subscriptionStatus", {
                          status,
                        })
                      );
                      await refresh();
                    } catch (e: any) {
                      toast.error(
                        e?.message ?? t("account.account.toast.failed")
                      );
                    }
                  }}
                />
              </TabsContent>

              <TabsContent value="events" className="mt-4">
                <div className="rounded-2xl border bg-card p-6">
                  <h3 className="text-lg font-semibold">
                    My Registered Events
                  </h3>
                  <p className="mb-6 mt-1 text-sm text-muted-foreground">
                    A list of upcoming and past community events you have
                    registered for.
                  </p>

                  {!registrations.length ? (
                    <div className="rounded-2xl border border-dashed p-10 text-center">
                      <ClipboardList className="text-muted-foreground/60 mx-auto h-12 w-12" />
                      <h3 className="mt-4 text-lg font-semibold">
                        No event registrations yet
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground">
                        You haven't registered for any community events. Check
                        out our upcoming events!
                      </p>
                      <Button asChild className="mt-4" variant="outline">
                        <Link to="/events">Browse Events</Link>
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {registrations.map(reg => {
                        const evt = eventsMap[reg.event];
                        return (
                          <div
                            key={reg.id}
                            className="shadow-xs relative overflow-hidden rounded-2xl border bg-card p-6 transition-shadow hover:shadow-md"
                          >
                            <div className="absolute left-0 top-0 h-full w-2 bg-[#A66E19]" />
                            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                              <div className="space-y-2 pl-2">
                                <span className="inline-block rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                                  Ticket #{reg.id}
                                </span>
                                <h4 className="font-serif text-lg font-bold text-foreground">
                                  {evt?.event_title ?? `Event #${reg.event}`}
                                </h4>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3.5 w-3.5" />
                                    {evt?.date
                                      ? new Date(evt.date).toLocaleDateString(
                                          "en-IN"
                                        )
                                      : "—"}
                                  </span>
                                  {evt?.venue && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="h-3.5 w-3.5" />
                                      {evt.venue}
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3.5 w-3.5" />
                                    {reg.total_attendees}{" "}
                                    {reg.total_attendees > 1
                                      ? "Attendees"
                                      : "Attendee"}
                                  </span>
                                </div>
                                {reg.notes && (
                                  <div className="bg-muted/50 mt-2 flex items-start gap-1 rounded-lg p-2 text-xs text-muted-foreground">
                                    <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    <span>
                                      <strong>Notes:</strong> {reg.notes}
                                    </span>
                                  </div>
                                )}
                              </div>

                              <div className="flex shrink-0 flex-col items-start gap-2 md:items-end">
                                <span className="text-xs text-muted-foreground">
                                  Registered on:{" "}
                                  {new Date(reg.created_at).toLocaleDateString(
                                    "en-IN"
                                  )}
                                </span>
                                {reg.image && (
                                  <a
                                    href={reg.image}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center text-xs text-[#A66E19] hover:underline"
                                  >
                                    View Attached Pass/Image
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="profile" className="mt-4">
                <div className="space-y-6">
                  {/* Header section with Edit Button */}
                  <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
                    <div>
                      <h3 className="text-xl font-bold text-primary">
                        Profile Information
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        View and manage your personal info, contact numbers, and
                        primary addresses.
                      </p>
                    </div>
                    <Button
                      onClick={() => setIsEditOpen(true)}
                      className="hover:bg-primary/95 rounded-full bg-primary shadow-sm"
                    >
                      <Edit className="mr-2 h-4 w-4" /> Edit Profile
                    </Button>
                  </div>

                  {/* Notice banner if no donor profile */}
                  {/* {!donor && (
                    <div className="rounded-xl border border-dashed border-amber-500/30 bg-amber-500/5 p-4 text-xs text-amber-700">
                      <p className="font-bold">No Donor Profile Linked Yet</p>
                      <p className="mt-1">
                        Make your first donation and a tax receipt donor profile will be automatically linked to your account.
                      </p>
                    </div>
                  )} */}

                  {/* Profile Completion Progress Card */}
                  <div className="via-primary/5 relative overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50/50 to-amber-50/30 p-6 shadow-sm">
                    {/* Background gold decorative blur */}
                    <div className="bg-gold/10 absolute right-0 top-0 -mr-6 -mt-6 h-24 w-24 animate-pulse rounded-full blur-xl" />

                    <div className="relative z-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                              completionPercentage === 100
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-amber-200 bg-amber-50 text-amber-700"
                            }`}
                          >
                            <Sparkles className="duration-3000 h-3 w-3 animate-spin" />
                            {completionPercentage === 100
                              ? "Verified Profile"
                              : "Profile Strength"}
                          </span>
                        </div>
                        <h4 className="font-serif text-xl font-bold leading-tight text-foreground">
                          {completionPercentage === 100
                            ? "Your profile is 100% complete!"
                            : "Complete your profile details to place orders"}
                        </h4>
                        <p className="max-w-md text-xs text-muted-foreground">
                          {completionPercentage === 100
                            ? "Your profile details are fully complete. Thank you!"
                            : "Please fill in your primary address, city, state, and pincode in your profile details to unlock ordering snacks."}
                        </p>
                      </div>

                      <div className="flex min-w-[200px] flex-col gap-2 sm:items-end">
                        <div className="flex w-full items-baseline justify-between gap-2 sm:justify-end">
                          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Completion:
                          </span>
                          <span
                            className={`text-3xl font-black ${
                              completionPercentage === 100
                                ? "text-emerald-600"
                                : "text-primary"
                            }`}
                          >
                            {completionPercentage}%
                          </span>
                        </div>
                        <div className="border-border/40 relative h-3.5 w-full overflow-hidden rounded-full border bg-secondary">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ease-out ${
                              completionPercentage === 100
                                ? "bg-gradient-to-r from-emerald-500 to-teal-500"
                                : "bg-gradient-to-r from-primary to-amber-500"
                            }`}
                            style={{ width: `${completionPercentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Profile Cards Grid */}
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Card 1: Personal Details */}
                    <div className="shadow-xs rounded-2xl border bg-card p-6">
                      <h4 className="mb-5 flex items-center gap-2 border-b border-dashed pb-2 font-serif text-lg font-bold text-[#A66E19]">
                        <UserIcon className="h-5 w-5 shrink-0 text-amber-500" />
                        Personal Information
                      </h4>
                      <div className="grid gap-x-4 gap-y-4 text-sm sm:grid-cols-2">
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Full Name
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.full_name ?? donor?.full_name ?? name}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            First Name
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.first_name ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Last Name
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.last_name ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Username
                          </span>
                          <span className="mt-1 block break-all font-medium text-foreground">
                            {authUser?.username ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Mobile Number
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.phone_number ?? donor?.mobile ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Email Address
                          </span>
                          <span className="mt-1 block break-all font-medium text-foreground">
                            {authUser?.email ?? donor?.email ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Gender
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.gender ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Date of Birth
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.date_of_birth ??
                              donor?.date_of_birth ??
                              "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            PAN Number
                          </span>
                          <span className="mt-1 block font-mono font-medium uppercase text-foreground">
                            {donor?.pan ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Country
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {donor?.country ?? "India"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card 2: User Address Details */}
                    <div className="shadow-xs rounded-2xl border bg-card p-6">
                      <h4 className="mb-5 flex items-center gap-2 border-b border-dashed pb-2 font-serif text-lg font-bold text-[#A66E19]">
                        <MapPin className="h-5 w-5 shrink-0 text-amber-500" />
                        User Details Address
                      </h4>
                      <div className="grid gap-x-4 gap-y-4 text-sm sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Address Line 1
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.address_line_1 ?? "—"}
                          </span>
                        </div>
                        <div className="sm:col-span-2">
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Address Line 2
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.address_line_2 ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Landmark
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.landmark ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Alternate Phone
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.alternate_phone_number ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            City
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.city ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            State
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.state ?? "—"}
                          </span>
                        </div>
                        <div className="sm:col-span-2">
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Pincode
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.pincode ?? "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card 3: Donor Address Details (Conditional) */}
                    {donor && (
                      <div className="shadow-xs rounded-2xl border bg-card p-6 md:col-span-2">
                        <h4 className="mb-5 flex items-center gap-2 border-b border-dashed pb-2 font-serif text-lg font-bold text-[#A66E19]">
                          <Receipt className="h-5 w-5 shrink-0 text-amber-500" />
                          Billing Donor Address (For Tax Receipt & 80G Benefit)
                        </h4>
                        <div className="grid gap-x-4 gap-y-4 text-sm sm:grid-cols-2">
                          <div className="sm:col-span-2">
                            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Billing Address
                            </span>
                            <span className="mt-1 block font-medium text-foreground">
                              {donor?.address ?? "—"}
                            </span>
                          </div>
                          <div>
                            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              City
                            </span>
                            <span className="mt-1 block font-medium text-foreground">
                              {donor?.city ?? "—"}
                            </span>
                          </div>
                          <div>
                            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              State
                            </span>
                            <span className="mt-1 block font-medium text-foreground">
                              {donor?.state ?? "—"}
                            </span>
                          </div>
                          <div>
                            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Pincode
                            </span>
                            <span className="mt-1 block font-medium text-foreground">
                              {donor?.pincode ?? "—"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Edit Profile Modal Dialog */}
                <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                  <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white p-6 sm:rounded-2xl">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-bold text-primary">
                        Edit Profile Details
                      </DialogTitle>
                    </DialogHeader>
                    <ProfileForm
                      donor={donor}
                      userDetails={userDetails}
                      onSave={async (
                        donorPayload,
                        userDetailsPayload,
                        personalPayload
                      ) => {
                        try {
                          if (donor) {
                            await updateDonorProfile({ data: donorPayload });
                          }
                          await userDetailsService.save(userDetailsPayload);
                          if (authUser?.id) {
                            updateSessionUser({
                              first_name: personalPayload.first_name,
                              last_name: personalPayload.last_name,
                              full_name: personalPayload.full_name,
                              phone_number: personalPayload.phone_number,
                              gender: personalPayload.gender,
                              date_of_birth: personalPayload.date_of_birth,
                            });
                          }
                          toast.success(
                            t("account.account.toast.profileUpdated")
                          );
                          setIsEditOpen(false);
                          await refresh();
                        } catch (e: any) {
                          toast.error(
                            e?.message ?? t("account.account.toast.failed")
                          );
                        }
                      }}
                      onCancel={() => setIsEditOpen(false)}
                    />
                  </DialogContent>
                </Dialog>
              </TabsContent>
            </Tabs>
          </section>
        </>
      )}
    </PageShell>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        <span className="bg-primary/10 grid h-7 w-7 place-items-center rounded-lg text-primary">
          {icon}
        </span>
        <span className="text-xs font-medium uppercase tracking-wider">
          {label}
        </span>
      </div>
      <p className="mt-2 text-2xl font-bold">{value}</p>
    </div>
  );
}

function DonationsTable({
  rows,
  onDownload,
}: {
  rows: any[];
  onDownload: (d: any) => void;
}) {
  const { t } = useI18n();
  if (!rows.length) {
    return (
      <div className="rounded-2xl border bg-card p-10 text-center">
        <p className="text-muted-foreground">
          {t("account.account.donationsTable.empty")}
        </p>
        <Button asChild className="mt-4">
          <Link to="/donate">
            {t("account.account.donationsTable.makeFirst")}
          </Link>
        </Button>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-3 text-left">
                {t("account.account.donationsTable.date")}
              </th>
              <th className="p-3 text-left">
                {t("account.account.donationsTable.receipt")}
              </th>
              <th className="p-3 text-left">
                {t("account.account.donationsTable.cause")}
              </th>
              <th className="p-3 text-left">
                {t("account.account.donationsTable.mode")}
              </th>
              <th className="p-3 text-right">
                {t("account.account.donationsTable.amount")}
              </th>
              <th className="p-3 text-left">
                {t("account.account.donationsTable.status")}
              </th>
              <th className="p-3 text-right">
                {t("account.account.donationsTable.receiptAction")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(d => (
              <tr key={d.id} className="hover:bg-muted/20 border-t">
                <td className="whitespace-nowrap p-3">
                  {new Date(d.donation_date).toLocaleDateString("en-IN")}
                </td>
                <td className="p-3 font-mono text-xs">{d.receipt_no}</td>
                <td className="p-3">{d.purpose ?? d.category ?? "—"}</td>
                <td className="p-3 capitalize">{d.payment_mode ?? "—"}</td>
                <td className="p-3 text-right font-semibold">
                  {inr(d.amount)}
                </td>
                <td className="p-3">
                  <Badge
                    variant={
                      d.payment_status === "success"
                        ? "default"
                        : d.payment_status === "pending"
                          ? "secondary"
                          : "destructive"
                    }
                  >
                    {d.payment_status}
                  </Badge>
                </td>
                <td className="p-3 text-right">
                  {d.payment_status === "success" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDownload(d)}
                    >
                      <Receipt className="mr-1 h-3.5 w-3.5" />
                      {t("account.account.donationsTable.download")}
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {t("account.account.donationsTable.pending")}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecurringTable({
  rows,
  onAction,
}: {
  rows: any[];
  onAction: (id: string, status: "active" | "paused" | "cancelled") => void;
}) {
  const { t } = useI18n();
  if (!rows.length) {
    return (
      <div className="rounded-2xl border bg-card p-10 text-center">
        <p className="text-muted-foreground">
          {t("account.account.recurringTable.empty")}
        </p>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
          <tr>
            <th className="p-3 text-left">
              {t("account.account.recurringTable.frequency")}
            </th>
            <th className="p-3 text-right">
              {t("account.account.recurringTable.amount")}
            </th>
            <th className="p-3 text-left">
              {t("account.account.recurringTable.nextPayment")}
            </th>
            <th className="p-3 text-left">
              {t("account.account.recurringTable.started")}
            </th>
            <th className="p-3 text-left">
              {t("account.account.recurringTable.status")}
            </th>
            <th className="p-3 text-right">
              {t("account.account.recurringTable.actions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-t">
              <td className="p-3 capitalize">{r.frequency}</td>
              <td className="p-3 text-right font-semibold">{inr(r.amount)}</td>
              <td className="p-3">
                {r.next_payment_date
                  ? new Date(r.next_payment_date).toLocaleDateString("en-IN")
                  : "—"}
              </td>
              <td className="p-3">
                {new Date(r.start_date).toLocaleDateString("en-IN")}
              </td>
              <td className="p-3">
                <Badge
                  variant={
                    r.status === "active"
                      ? "default"
                      : r.status === "paused"
                        ? "secondary"
                        : "destructive"
                  }
                >
                  {r.status}
                </Badge>
              </td>
              <td className="space-x-1 p-3 text-right">
                {r.status === "active" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onAction(r.id, "paused")}
                  >
                    <Pause className="mr-1 h-3.5 w-3.5" />
                    {t("account.account.recurringTable.pause")}
                  </Button>
                )}
                {r.status === "paused" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onAction(r.id, "active")}
                  >
                    <Play className="mr-1 h-3.5 w-3.5" />
                    {t("account.account.recurringTable.resume")}
                  </Button>
                )}
                {r.status !== "cancelled" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onAction(r.id, "cancelled")}
                  >
                    <XCircle className="mr-1 h-3.5 w-3.5" />
                    {t("account.account.recurringTable.cancel")}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProfileForm({
  donor,
  userDetails,
  onSave,
  onCancel,
}: {
  donor: any;
  userDetails: any;
  onSave: (
    donorPayload: any,
    userDetailsPayload: any,
    personalPayload: any
  ) => void | Promise<void>;
  onCancel?: () => void;
}) {
  const { t } = useI18n();
  const { user: authUser } = useAuth();

  const [personal, setPersonal] = useState({
    first_name: authUser?.first_name ?? "",
    last_name: authUser?.last_name ?? "",
    full_name: authUser?.full_name ?? "",
    email: authUser?.email ?? "",
    phone_number: authUser?.phone_number ?? "",
    gender: authUser?.gender ?? "",
    date_of_birth: authUser?.date_of_birth ?? "",
  });

  const [f, setF] = useState({
    full_name: donor?.full_name ?? "",
    mobile: donor?.mobile ?? "",
    email: donor?.email ?? "",
    pan: donor?.pan ?? "",
    address: donor?.address ?? "",
    city: donor?.city ?? "",
    state: donor?.state ?? "",
    pincode: donor?.pincode ?? "",
    country: donor?.country ?? "India",
    date_of_birth: donor?.date_of_birth ?? "",
  });

  const [u, setU] = useState({
    id: userDetails?.id,
    address_line_1: userDetails?.address_line_1 ?? "",
    address_line_2: userDetails?.address_line_2 ?? "",
    landmark: userDetails?.landmark ?? "",
    city: userDetails?.city ?? "",
    state: userDetails?.state ?? "",
    pincode: userDetails?.pincode ?? "",
    alternate_phone_number: userDetails?.alternate_phone_number ?? "",
  });

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authUser) {
      setPersonal({
        first_name: authUser.first_name ?? "",
        last_name: authUser.last_name ?? "",
        full_name: authUser.full_name ?? "",
        email: authUser.email ?? "",
        phone_number: authUser.phone_number ?? "",
        gender: authUser.gender ?? "",
        date_of_birth: authUser.date_of_birth ?? "",
      });
    }
  }, [authUser]);

  useEffect(() => {
    if (donor) {
      setF({
        full_name: donor.full_name ?? "",
        mobile: donor.mobile ?? "",
        email: donor.email ?? "",
        pan: donor.pan ?? "",
        address: donor.address ?? "",
        city: donor.city ?? "",
        state: donor.state ?? "",
        pincode: donor.pincode ?? "",
        country: donor.country ?? "India",
        date_of_birth: donor.date_of_birth ?? "",
      });
    }
  }, [donor]);

  useEffect(() => {
    if (userDetails) {
      setU({
        id: userDetails.id,
        address_line_1: userDetails.address_line_1 ?? "",
        address_line_2: userDetails.address_line_2 ?? "",
        landmark: userDetails.landmark ?? "",
        city: userDetails.city ?? "",
        state: userDetails.state ?? "",
        pincode: userDetails.pincode ?? "",
        alternate_phone_number: userDetails.alternate_phone_number ?? "",
      });
    }
  }, [userDetails]);

  return (
    <form
      className="space-y-6"
      onSubmit={async e => {
        e.preventDefault();
        setBusy(true);
        try {
          await onSave(f, u, personal);
        } finally {
          setBusy(false);
        }
      }}
    >
      {/* Personal Information Section */}
      <div>
        <h3 className="text-lg font-semibold text-primary">
          Personal Information
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Update your personal details including name, phone, date of birth and
          gender.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="First Name *">
            <Input
              required
              value={personal.first_name}
              onChange={e =>
                setPersonal({ ...personal, first_name: e.target.value })
              }
              placeholder="First Name"
            />
          </Field>
          <Field label="Last Name">
            <Input
              value={personal.last_name}
              onChange={e =>
                setPersonal({ ...personal, last_name: e.target.value })
              }
              placeholder="Last Name"
            />
          </Field>
          <Field label="Full Name">
            <Input
              value={personal.full_name}
              onChange={e =>
                setPersonal({ ...personal, full_name: e.target.value })
              }
              placeholder="Full Name"
            />
          </Field>
          <Field label="Mobile Number">
            <Input
              value={personal.phone_number}
              onChange={e =>
                setPersonal({ ...personal, phone_number: e.target.value })
              }
              placeholder="Mobile Number"
            />
          </Field>
          <Field label="Email Address">
            <Input
              type="email"
              value={personal.email}
              disabled
              className="bg-muted opacity-70"
            />
          </Field>
          <Field label="Gender">
            <select
              value={personal.gender}
              onChange={e =>
                setPersonal({ ...personal, gender: e.target.value })
              }
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </Field>
          <Field label="Date of Birth">
            <Input
              type="date"
              value={personal.date_of_birth}
              onChange={e =>
                setPersonal({ ...personal, date_of_birth: e.target.value })
              }
            />
          </Field>
          <Field label="Username">
            <Input
              value={authUser?.username ?? ""}
              disabled
              className="bg-muted opacity-70"
            />
          </Field>
        </div>
      </div>

      <div className="my-6 border-t border-border" />

      {donor ? (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-primary">
            {t("account.account.profile.title")}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("account.account.profile.description")}
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label={t("account.account.profile.fullName")}>
              <Input
                required
                value={f.full_name}
                onChange={e => setF({ ...f, full_name: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.mobile")}>
              <Input
                required
                value={f.mobile}
                onChange={e => setF({ ...f, mobile: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.email")}>
              <Input
                type="email"
                value={f.email}
                onChange={e => setF({ ...f, email: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.pan")}>
              <Input
                value={f.pan}
                onChange={e =>
                  setF({ ...f, pan: e.target.value.toUpperCase() })
                }
                placeholder={t("account.account.profile.panPlaceholder")}
              />
            </Field>
            <Field label={t("account.account.profile.dateOfBirth")}>
              <Input
                type="date"
                value={f.date_of_birth ?? ""}
                onChange={e => setF({ ...f, date_of_birth: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.country")}>
              <Input
                value={f.country}
                onChange={e => setF({ ...f, country: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.address")} full>
              <Textarea
                rows={2}
                value={f.address}
                onChange={e => setF({ ...f, address: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.city")}>
              <Input
                value={f.city}
                onChange={e => setF({ ...f, city: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.state")}>
              <Input
                value={f.state}
                onChange={e => setF({ ...f, state: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.pincode")}>
              <Input
                value={f.pincode}
                onChange={e => setF({ ...f, pincode: e.target.value })}
              />
            </Field>
          </div>
          <div className="my-6 border-t border-border" />
        </div>
      ) : (
        <div className="mb-6 rounded-xl border border-dashed border-amber-500/30 bg-amber-500/5 p-4 text-xs text-amber-700">
          <p className="font-bold">No Donor Profile Linked Yet</p>
          <p className="mt-1">
            Make your first donation and a tax receipt donor profile will be
            automatically linked to your account.
          </p>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold text-primary">
          User Profile Details
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Update your personal contact numbers and primary residence address
          details.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Address Line 1">
            <Input
              value={u.address_line_1}
              onChange={e => setU({ ...u, address_line_1: e.target.value })}
              placeholder="House, apartment, building number"
            />
          </Field>
          <Field label="Address Line 2">
            <Input
              value={u.address_line_2}
              onChange={e => setU({ ...u, address_line_2: e.target.value })}
              placeholder="Street, area name"
            />
          </Field>
          <Field label="Landmark">
            <Input
              value={u.landmark}
              onChange={e => setU({ ...u, landmark: e.target.value })}
              placeholder="e.g. Near Jain Temple"
            />
          </Field>
          <Field label="Alternate Phone Number">
            <Input
              value={u.alternate_phone_number}
              onChange={e =>
                setU({ ...u, alternate_phone_number: e.target.value })
              }
              placeholder="Alternate mobile or landline number"
            />
          </Field>
          <Field label="City">
            <Input
              value={u.city}
              onChange={e => setU({ ...u, city: e.target.value })}
              placeholder="City"
            />
          </Field>
          <Field label="State">
            <Input
              value={u.state}
              onChange={e => setU({ ...u, state: e.target.value })}
              placeholder="State"
            />
          </Field>
          <Field label="Pincode">
            <Input
              value={u.pincode}
              onChange={e => setU({ ...u, pincode: e.target.value })}
              placeholder="Pincode"
            />
          </Field>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3 border-t pt-4">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={busy}>
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {t("account.account.profile.save")}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label>{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
